// Utils
import { generateRequest } from '../http.service';
import { getOddsParams, getLanguageParam, getTabValue } from '../../utils/';

// Types
import { SDKOptions, IOddsBaseConfig, SportTypes, IMultiSportTournamentResponse } from '../../types';
import { IMultiSportEventsResponse, IMultiSportEventResponse } from '../../types';

// Constants
import { POPULAR } from '../../constants/statusFilters';

export interface IEventProps extends IOddsBaseConfig {
  idOrSlug?: string;
  sport: string;
}

export const getMultiSportEvent = async ({
  params,
  sdkOptions,
}: {
  params: IEventProps;
  sdkOptions: SDKOptions;
}): Promise<IMultiSportEventResponse> => {
  const url = `${sdkOptions.dataConfigMultisportApiUrl}/multi-sport/events/${params.idOrSlug}`;

  const baseParams = {
    sport: params.sport,
    translation_language: getLanguageParam(sdkOptions.dataConfigLang),
  };

  const formatOddsParams = getOddsParams({
    dataConfigOddClient: sdkOptions.dataConfigOddClient,
    ...params,
  });

  const oddsParams = {
    odd_client: formatOddsParams.oddClient ?? undefined,
    odd_format: formatOddsParams.oddFormat ?? undefined,
    market_types: formatOddsParams.marketTypes ?? undefined,
    odd_type: formatOddsParams.oddType ?? undefined,
  };

  const requestParams = {
    ...baseParams,
    ...oddsParams,
  };

  return await generateRequest(url, requestParams, sdkOptions);
};

export interface IEventsProps extends IOddsBaseConfig {
  statusFilter: string;
  date: string;
  utcOffset: string;
  competitionIds?: string[] | null;
  dataPopularList?: string;
  sport?: string;
}

export const getMultiSportEvents = async ({
  params,
  sdkOptions,
}: {
  params: IEventsProps;
  sdkOptions: SDKOptions;
}): Promise<IMultiSportEventsResponse> => {
  const sportCategory = params.sport === SportTypes.ICE_HOCKEY || params.sport === SportTypes.FOOTBALL ? 'multi-sport' : params.sport;
  const url = `${sdkOptions.dataConfigMultisportApiUrl}/${sportCategory}/events`;

  const baseParams = {
    date: params.date,
    utc_offset: params.utcOffset,
    sport: params.sport,
    status_type: getTabValue(params.statusFilter),
    competition_list: params.statusFilter === POPULAR ? params.dataPopularList : sdkOptions.dataConfigCompetitionList,
    competition_ids: params.competitionIds && params.competitionIds.length > 0 ? params.competitionIds : undefined,
    translation_language: getLanguageParam(sdkOptions.dataConfigLang),
  };

  const formatOddsParams = getOddsParams({
    dataConfigOddClient: sdkOptions.dataConfigOddClient,
    ...params,
  });

  const oddsParams = {
    odd_client: formatOddsParams.oddClient ?? undefined,
    odd_format: formatOddsParams.oddFormat ?? undefined,
    market_types: formatOddsParams.marketTypes ?? undefined,
    odd_type: formatOddsParams.oddType ?? undefined,
  };

  const requestParams = {
    ...baseParams,
    ...oddsParams,
  };

  return await generateRequest(url, requestParams, sdkOptions);
};

export interface IProgrammeEventsProps extends IOddsBaseConfig {
  competition_ids?: string;
  team_ids?: string;
  status_type: string;
  season_ids?: string;
  stage_ids?: string;
  round_filter?: string[];
  sort_order?: string;
  from_start_time?: string;
  to_start_time?: string;
  limit?: string;
  sport?: string;
}

export const getMultiSportProgrammeEvents = async ({
  params,
  sdkOptions,
}: {
  params: IProgrammeEventsProps;
  sdkOptions: SDKOptions;
}): Promise<IMultiSportEventsResponse> => {
  const url = `${sdkOptions.dataConfigMultisportApiUrl}/multi-sport/events/search`;

  const baseParams = {
    translation_language: getLanguageParam(sdkOptions.dataConfigLang),
    competition_ids: params.competition_ids,
    participants_filter: params.team_ids,
    status_type: params.status_type,
    season_ids: params.season_ids,
    stage_ids: params.stage_ids,
    round_filter: params.round_filter,
    sort_direction: params.sort_order,
    from_start_time: params.from_start_time,
    to_start_time: params.to_start_time,
    offset: 0,
    limit: params.limit,
    sport: params.sport,
    date_grouping: true,
  };

  const formatOddsParams = getOddsParams({
    dataConfigOddClient: sdkOptions.dataConfigOddClient,
    ...params,
  });

  const oddsParams = {
    odd_client: formatOddsParams.oddClient ?? undefined,
    odd_format: formatOddsParams.oddFormat ?? undefined,
    market_types: formatOddsParams.marketTypes ?? undefined,
    odd_type: formatOddsParams.oddType ?? undefined,
  };

  const requestParams = {
    ...baseParams,
    ...oddsParams,
  };

  return await generateRequest(url, requestParams, sdkOptions);
};

export const getMultiSportTournamentProgrammeEvents = async ({
  params,
  sdkOptions,
}: {
  params: IProgrammeEventsProps;
  sdkOptions: SDKOptions;
}): Promise<IMultiSportTournamentResponse> => {
  const url = `${sdkOptions.dataConfigMultisportApiUrl}/multi-sport/events/rounds`;

  const baseParams = {
    translation_language: getLanguageParam(sdkOptions.dataConfigLang),
    competition_ids: params.competition_ids,
    participants_filter: params.team_ids,
    status_type: params.status_type,
    season_ids: params.season_ids,
    stage_ids: params.stage_ids,
    round_filter: params.round_filter,
    sort_direction: params.sort_order,
    from_start_time: params.from_start_time,
    to_start_time: params.to_start_time,
    offset: 0,
    limit: params.limit,
    sport: params.sport,
  };

  const formatOddsParams = getOddsParams({
    dataConfigOddClient: sdkOptions.dataConfigOddClient,
    ...params,
  });

  const oddsParams = {
    odd_client: formatOddsParams.oddClient ?? undefined,
    odd_format: formatOddsParams.oddFormat ?? undefined,
    market_types: formatOddsParams.marketTypes ?? undefined,
    odd_type: formatOddsParams.oddType ?? undefined,
  };

  const requestParams = {
    ...baseParams,
    ...oddsParams,
  };

  return await generateRequest(url, requestParams, sdkOptions);
};
